/**
 * deskExport — client-side CSV of the CURRENTLY FILTERED admission rows (C6a).
 * No network, no new permission: the server's VIEW_ADMISSION already gated the
 * read that produced these rows, so exporting the same denormalized fields adds
 * no new data exposure (architect: no EXPORT_ADMISSION theatre). Dues columns are
 * intentionally ABSENT — payment lands in C6b.
 *
 * Columns are FROZEN (order + labels) so downstream sheets/macros don't break.
 */

import {
  ADMISSION_STATUS_LABELS,
  PAYER_TYPE_LABELS,
  daysAdmitted,
  isAwaitingBed,
} from '../../constants/admission';

const COLUMNS = [
  'IP No',
  'Patient',
  'Status',
  'Doctor',
  'Department',
  'Ward/Bed',
  'Payer',
  'MLC',
  'Admitted At',
  'Days Admitted',
  'Attender Name',
  'Attender Phone',
];

/** RFC-4180 field escape: wrap in quotes iff it holds a comma/quote/newline. */
function csvCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function wardBedCell(a) {
  // ward_name = BE denorm stamped with bed_label (C6a); null on legacy rows.
  if (a.bed_label) return a.ward_name ? `${a.ward_name} ${a.bed_label}` : a.bed_label;
  if (isAwaitingBed(a)) return 'Awaiting bed';
  return 'No bed';
}

function rowToCells(a) {
  const attender = a.attender || null;
  const los = daysAdmitted(a);
  return [
    a.ip_number || '',
    a.patient_name || a.patient_id || '',
    ADMISSION_STATUS_LABELS[a.status] || a.status || '',
    a.primary_doctor_name || 'No doctor',
    a.department_name || 'No department',
    wardBedCell(a),
    PAYER_TYPE_LABELS[a.payer_type] || a.payer_type || '',
    a.is_mlc ? 'Yes' : 'No',
    a.admitted_at ? new Date(a.admitted_at).toLocaleString() : 'Not admitted',
    los == null ? '' : String(los),
    attender && attender.name ? attender.name : 'No attender recorded',
    attender ? (attender.phone || attender.mobile || '') : '',
  ];
}

/** Build the CSV string (header + one line per filtered row). Deterministic. */
export function buildAdmissionsCsv(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const lines = [COLUMNS.map(csvCell).join(',')];
  for (const a of list) {
    lines.push(rowToCells(a).map(csvCell).join(','));
  }
  return lines.join('\r\n');
}

/**
 * discharge-perf F5 (D6a, LOCKED) — the Discharged bucket's exported filename
 * carries its date range, taken from the SERVER-echoed `period` ONLY (never
 * recomputed client-side). Every other bucket keeps its existing
 * `admissions-<yyyy-mm-dd>.csv` name — `period` is null everywhere except the
 * Discharged window view (see AdmissionDeskPage's `period` state), so this is
 * additive: no other bucket's call site changes.
 */
function csvFileName(period) {
  if (period && period.from && period.to) {
    return `discharged_${period.from}_to_${period.to}.csv`;
  }
  const now = new Date();
  const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return `admissions-${stamp}.csv`;
}

/**
 * Trigger a browser download of the filtered rows. `period` (optional,
 * additive) is the server-echoed `{from, to}` for the Discharged window view
 * — it only ever changes the FILE NAME (D6a); the CSV body/layout below is
 * byte-identical regardless.
 */
export function downloadAdmissionsCsv(rows, period) {
  const csv = buildAdmissionsCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = csvFileName(period);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const ADMISSION_CSV_COLUMNS = COLUMNS;
